import {
  CatalogImages,
  CatalogImagesDegrees,
  CatalogImagesPi,
  CatalogImagesPiPaths,
} from '../model/product'
import { isIPadView } from './url'
import { CartProduct } from '../model/cart'
import { IMAGES_BASE_URL } from '../constants'

export const getProductImageUrl = ({
  path,
  imwidth = 600,
  scale,
}: {
  path: string
  imwidth?: number
  scale?: boolean
}) => {
  // scale directly proportional to innerWidth but round to nearest hundred
  const imwidthScaled = scale
    ? Math.round((window.innerWidth * imwidth) / 1366 / 100) * 100
    : imwidth
  return `${IMAGES_BASE_URL}${path}?imwidth=${imwidthScaled}`
}
export const getProductImageAngledWithFallback = ({
  catalogImages,
  angle,
  imwidth = 300,
  shadow,
}: {
  catalogImages?: CatalogImages | CartProduct
  angle: 'quarter' | 'front'
  imwidth?: number
  shadow?: boolean
}) => {
  const getPath = () => {
    if (!catalogImages) {
      return ''
    }
    // the BE can send a pi21/pi20 array without the requested angle (e.g. only Alternative
    // transitions images): the angle is then converted with empty paths, so we fall back
    // to the next source only when the path is actually set
    const pi21Path =
      'pi21' in catalogImages &&
      (shadow ? catalogImages.pi21?.[angle]?.shadow : catalogImages.pi21?.[angle]?.base)
    const pi20Path =
      'pi20' in catalogImages &&
      (shadow ? catalogImages.pi20?.[angle]?.shadow : catalogImages.pi20?.[angle]?.base)
    const picPath = 'pic' in catalogImages && catalogImages.pic?.[angle]?.base
    const degree = angle === 'quarter' ? '030' : '000'
    const degreePath = 'degrees' in catalogImages && catalogImages.degrees?.[degree]

    return (
      pi21Path ||
      pi20Path ||
      picPath ||
      degreePath ||
      ('base' in catalogImages ? catalogImages.base || '' : catalogImages.urlImage || '')
    )
  }

  const productPath = getPath()
  const url = getProductImageUrl({ path: productPath, imwidth })
  return url
}
export const getDegrees = (degrees?: CatalogImagesDegrees, fullscreen = false) => {
  if (!degrees || !Object.keys(degrees).length) return []
  const imwidth = isIPadView() ? (fullscreen ? 1500 : 890) : 1500
  const allDegrees = [
    '000',
    '030',
    '060',
    '090',
    '120',
    '150',
    '180',
    '210',
    '240',
    '270',
    '300',
    '330',
  ] as (keyof CatalogImagesDegrees)[]

  const images: { preview: string; file: string; angle: number }[] = []

  for (let i = 0; i < allDegrees.length; i++) {
    const degree = allDegrees[i]
    if (degrees[degree]) {
      const url = getProductImageUrl({ path: degrees[degree], imwidth })
      images.push({
        preview: url,
        file: url,
        angle: parseInt(degree, 10),
      })
    }
  }

  return images
}

interface GetPiOptions {
  images?: CatalogImagesPi
  fullscreen?: boolean
  transitionImageInPlp?: boolean
}

export const getImagesFor360 = (options: GetPiOptions) => {
  const { images, fullscreen = false, transitionImageInPlp } = options
  const imwidth = isIPadView() ? (fullscreen ? 1500 : 890) : 1500
  const sortOder = [
    transitionImageInPlp && 'quarterTransitions',
    'quarter',
    'lateral',
    'back',
    'frontTransitions',
    'front',
    'closedFrontTransitions',
    'closedFront',
  ] as (keyof CatalogImagesPi)[]

  const isAngleConfigured = (item: CatalogImagesPiPaths) => {
    return (
      (item.base && item.base.trim() !== '') ||
      (item.shadow && item.shadow.trim() !== '') ||
      (item.transitions && item.transitions.trim() !== '')
    )
  }

  const hasShadowImage = (item: CatalogImagesPiPaths) => item.shadow && item.shadow.trim() !== ''

  const shadow = images
    ? Object.values(images).every(item => !isAngleConfigured(item) || hasShadowImage(item))
    : false

  const sortedPi = sortOder.filter(Boolean).reduce((acc, degree) => {
    const path = shadow ? images?.[degree]?.shadow || '' : images?.[degree]?.base || ''

    if (!path) return acc

    const url = getProductImageUrl({ path: path, imwidth })
    const alternativePath = (degree === 'quarter' && images?.[degree].transitions) || ''
    const transitions = alternativePath && getProductImageUrl({ path: alternativePath, imwidth })

    acc.push({
      preview: url,
      file: url,
      transitions,
    })

    return acc
  }, [] as Array<{ preview: string; file: string; transitions?: string }>)

  return sortedPi
}
